import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost, fetchAllPaginated } from '../services/api';
import { useEffectiveCity } from '../hooks/useEffectiveCity';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import SearchableSelect from '../components/SearchableSelect';

function RespostasAlunos() {
  const { darkMode } = useTheme();
  const effectiveCity = useEffectiveCity();
  const cityId = effectiveCity?.id || null;

  // Estados principais
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filtros e seleção
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal de registro de respostas
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAnswers, setStudentAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  // Gabarito oficial da prova selecionada
  const [answerKey, setAnswerKey] = useState([]);
  const [loadingAnswerKey, setLoadingAnswerKey] = useState(false);

  // Respostas existentes dos alunos
  const [existingResponses, setExistingResponses] = useState({});
  const [loadingResponses, setLoadingResponses] = useState(false);

  // Estatísticas
  const [stats, setStats] = useState({
    totalStudents: 0,
    withResponses: 0,
    pending: 0,
    averageScore: 0
  });

  // Buscar provas com gabarito
  const fetchExams = useCallback(async () => {
    try {
      const response = await apiGet('/exams/', { page_size: 100 });
      const examsList = response.results || [];

      // Filtrar apenas provas com gabarito
      const examsWithAnswerKey = await Promise.all(
        examsList.map(async (exam) => {
          try {
            const answerKeyData = await apiGet(`/exams/${exam.id}/answer_key/`);
            return {
              ...exam,
              hasAnswerKey: answerKeyData.has_answer_key,
              answersCount: answerKeyData.answers?.length || 0,
              answerKeyData: answerKeyData.answers || []
            };
          } catch {
            return { ...exam, hasAnswerKey: false, answersCount: 0, answerKeyData: [] };
          }
        })
      );

      setExams(examsWithAnswerKey.filter(e => e.hasAnswerKey));
    } catch (err) {
      console.error('Erro ao buscar provas:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao buscar provas.';
      setError(errorMessage);
    }
  }, []);

  // Buscar turmas
  const fetchClasses = useCallback(async () => {
    try {
      // Todas as páginas: só a primeira (100 turmas) deixava de fora o que
      // vinha depois em ordem alfabética.
      const turmas = await fetchAllPaginated('/classes/', cityId ? { city: cityId } : {});
      setClasses(turmas);
    } catch (err) {
      console.error('Erro ao buscar turmas:', err);
    }
  }, [cityId]);

  // Buscar alunos da turma selecionada
  const fetchStudents = useCallback(async () => {
    if (!selectedClass) {
      setStudents([]);
      return;
    }

    try {
      const response = await apiGet('/students/', {
        id_class: selectedClass.id,
        page_size: 100
      });
      setStudents(response.results || []);
    } catch (err) {
      console.error('Erro ao buscar alunos:', err);
    }
  }, [selectedClass]);

  // Buscar respostas existentes dos alunos para a prova selecionada
  const fetchExistingResponses = useCallback(async () => {
    if (!selectedExam || !selectedClass) {
      setExistingResponses({});
      return;
    }

    setLoadingResponses(true);
    try {
      // Buscar todas as respostas dos alunos para esta prova e turma
      const response = await apiGet('/student-answers/by_exam_class/', {
        exam_id: selectedExam.id,
        class_id: selectedClass.id
      });

      // Organizar por aluno
      const responsesByStudent = {};
      (response.results || []).forEach(answer => {
        const studentId = answer.id_student;
        if (!responsesByStudent[studentId]) {
          responsesByStudent[studentId] = {
            answers: {},
            correctCount: 0,
            totalAnswered: 0
          };
        }
        responsesByStudent[studentId].answers[answer.question_number] = {
          answer: answer.selected_answer,
          isCorrect: answer.is_correct
        };
        responsesByStudent[studentId].totalAnswered++;
        if (answer.is_correct) {
          responsesByStudent[studentId].correctCount++;
        }
      });

      setExistingResponses(responsesByStudent);

      // Calcular estatísticas
      const studentsWithResponses = Object.keys(responsesByStudent).length;
      const totalStudents = students.length;
      let totalCorrect = 0;
      let totalAnswered = 0;

      Object.values(responsesByStudent).forEach(data => {
        totalCorrect += data.correctCount;
        totalAnswered += data.totalAnswered;
      });

      setStats({
        totalStudents,
        withResponses: studentsWithResponses,
        pending: totalStudents - studentsWithResponses,
        averageScore: totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0
      });

    } catch (err) {
      console.error('Erro ao buscar respostas:', err);
    } finally {
      setLoadingResponses(false);
    }
  }, [selectedExam, selectedClass, students.length]);

  // Carregar dados iniciais
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchExams(), fetchClasses()]);
      setLoading(false);
    };
    loadData();
  }, [fetchExams, fetchClasses]);

  // Buscar alunos quando turma muda
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Buscar respostas quando prova ou turma muda
  useEffect(() => {
    if (students.length > 0) {
      fetchExistingResponses();
    }
  }, [selectedExam, selectedClass, students.length, fetchExistingResponses]);

  // Abrir modal para registrar respostas do aluno
  const handleOpenModal = async (student) => {
    setSelectedStudent(student);
    setStudentAnswers({});

    if (selectedExam) {
      setLoadingAnswerKey(true);
      try {
        const answerKeyData = await apiGet(`/exams/${selectedExam.id}/answer_key/`);
        setAnswerKey(answerKeyData.answers || []);

        // Carregar respostas existentes do aluno
        if (existingResponses[student.id_student]) {
          const existing = {};
          Object.entries(existingResponses[student.id_student].answers).forEach(([qNum, data]) => {
            existing[parseInt(qNum)] = data.answer;
          });
          setStudentAnswers(existing);
        }
      } catch (err) {
        console.error('Erro ao carregar gabarito:', err);
        setError('Erro ao carregar gabarito da prova.');
      } finally {
        setLoadingAnswerKey(false);
      }
    }

    setShowModal(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
    setStudentAnswers({});
    setAnswerKey([]);
  };

  // Selecionar resposta do aluno
  const handleSelectAnswer = (questionNumber, answer) => {
    setStudentAnswers(prev => ({
      ...prev,
      [questionNumber]: answer
    }));
  };

  // Salvar respostas do aluno
  const handleSave = async () => {
    if (!selectedStudent || !selectedExam) return;

    setSaving(true);
    setError(null);

    try {
      // Preparar dados das respostas
      const answersToSave = [];
      answerKey.forEach(q => {
        const studentAnswer = studentAnswers[q.question_number];
        if (studentAnswer) {
          answersToSave.push({
            question_number: q.question_number,
            selected_answer: studentAnswer,
            correct_answer: q.correct_answer,
            is_correct: studentAnswer === q.correct_answer,
            descriptor_id: q.descriptor_id || null
          });
        }
      });

      // Enviar para a API
      await apiPost('/student-answers/bulk_save/', {
        student_id: selectedStudent.id_student,
        exam_id: selectedExam.id,
        class_id: selectedClass.id,
        answers: answersToSave
      });

      setSuccess(`Respostas de ${selectedStudent.student_name} salvas com sucesso!`);
      handleCloseModal();
      fetchExistingResponses();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Erro ao salvar respostas:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Erro ao salvar respostas. Verifique suas permissões.';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Calcular resultado do aluno no modal
  const calculateResult = useMemo(() => {
    if (answerKey.length === 0) return { correct: 0, wrong: 0, blank: 0, percentage: 0 };

    let correct = 0;
    let wrong = 0;
    let blank = 0;

    answerKey.forEach(q => {
      const studentAnswer = studentAnswers[q.question_number];
      if (!studentAnswer) {
        blank++;
      } else if (studentAnswer === q.correct_answer) {
        correct++;
      } else {
        wrong++;
      }
    });

    return {
      correct,
      wrong,
      blank,
      percentage: answerKey.length > 0 ? Math.round((correct / answerKey.length) * 100) : 0
    };
  }, [studentAnswers, answerKey]);

  // Filtrar alunos
  const filteredStudents = useMemo(() => {
    if (!searchTerm) return students;
    const term = searchTerm.toLowerCase();
    return students.filter(s =>
      s.student_name?.toLowerCase().includes(term) ||
      s.student_serial?.toString().includes(term)
    );
  }, [students, searchTerm]);

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Respostas dos Alunos..."
        //subtitle={userCity ? `Buscando dados de ${userCity.name}...` : 'Buscando informações do banco de dados...'}
        size="xl"
        animationType="spin-pulse"
      />
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
            Respostas dos Alunos
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Cadastre as respostas de cada aluno nas provas aplicadas
          </p>
        </div>
      </header>

      {/* Mensagens de feedback */}
      {error && (
        <div className="mb-4 p-4 rounded-xl bg-[#E83A3C]/10 border border-[#E83A3C]/20 text-[#E83A3C]">
          {error}
          <button onClick={() => setError(null)} className="float-right font-bold">x</button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 rounded-xl bg-[#13983D]/10 border border-[#13983D]/20 text-[#13983D]">
          {success}
          <button onClick={() => setSuccess(null)} className="float-right font-bold">x</button>
        </div>
      )}

      {/* Seleção de Prova e Turma */}
      <div className={`rounded-xl p-6 mb-6 ${darkMode
        ? 'bg-slate-800/50 border border-slate-700/10'
        : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
        <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
          Selecione a Prova e a Turma
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Seleção de Prova */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Prova (com gabarito)
            </label>
            <SearchableSelect
              value={selectedExam?.id || ''}
              onChange={(v) => {
                const exam = exams.find(ex => ex.id === parseInt(v));
                setSelectedExam(exam || null);
              }}
              options={exams.map(exam => ({
                value: exam.id,
                label: `${exam.exam_code} - ${exam.exam_name} (${exam.answersCount} questões)`,
              }))}
              placeholder="Selecione uma prova..."
              darkMode={darkMode}
              className="w-full"
            />
          </div>

          {/* Seleção de Turma */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Turma
            </label>
            <SearchableSelect
              value={selectedClass?.id || ''}
              onChange={(v) => {
                const cls = classes.find(c => c.id === parseInt(v));
                setSelectedClass(cls || null);
              }}
              options={classes.map(cls => ({
                value: cls.id,
                label: `${cls.class_name} - ${cls.grade} (${cls.school_year})`,
              }))}
              placeholder="Selecione uma turma..."
              darkMode={darkMode}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      {selectedExam && selectedClass && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total de Alunos', value: stats.totalStudents, icon: '👥', color: 'blue' },
            { label: 'Com Respostas', value: stats.withResponses, icon: '✅', color: 'green' },
            { label: 'Pendentes', value: stats.pending, icon: '⏳', color: 'orange' },
            { label: 'Média de Acertos', value: `${stats.averageScore}%`, icon: '📊', color: 'purple' },
          ].map(stat => (
            <div
              key={stat.label}
              className={`rounded-xl p-4 ${darkMode
                ? 'bg-slate-800/50 border border-slate-700/10'
                : 'bg-white/80 border border-slate-200 shadow-sm'
                }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center text-xl ${stat.color === 'blue' ? 'bg-[#0974BA]/20' :
                  stat.color === 'green' ? 'bg-[#13983D]/20' :
                    stat.color === 'orange' ? 'bg-[#F9B047]/20' :
                      'bg-[#2E4974]/20'
                  }`}>
                  {stat.icon}
                </div>
                <div>
                  <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                    {stat.value}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {stat.label}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lista de Alunos */}
      {selectedExam && selectedClass && (
        <div className={`rounded-2xl overflow-hidden ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          {/* Barra de pesquisa */}
          <div className={`p-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="relative max-w-md">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              <input
                type="text"
                placeholder="Buscar aluno por nome ou matrícula..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-sm ${darkMode
                  ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-400'
                  : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                  } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
              />
            </div>
          </div>

          {/* Tabela de alunos */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={darkMode ? 'bg-slate-900/50' : 'bg-slate-50'}>
                <tr>
                  {['Matrícula', 'Nome do Aluno', 'Respostas', 'Acertos', 'Taxa', 'Ações'].map(h => (
                    <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-slate-700/50' : 'divide-slate-100'}`}>
                {loadingResponses ? (
                  <tr>
                    <td colSpan="6" className={`px-4 py-10 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
                        Carregando respostas...
                      </div>
                    </td>
                  </tr>
                ) : filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan="6" className={`px-4 py-10 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Nenhum aluno encontrado nesta turma
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((student) => {
                    const studentData = existingResponses[student.id_student];
                    const hasResponses = !!studentData;
                    const answeredCount = studentData?.totalAnswered || 0;
                    const correctCount = studentData?.correctCount || 0;
                    const totalQuestions = selectedExam.answersCount || 0;
                    const percentage = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;

                    return (
                      <tr key={student.id_student} className={darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}>
                        <td className={`px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          <span className="font-mono text-xs">{student.student_serial}</span>
                        </td>
                        <td className={`px-4 py-3 font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          {student.student_name}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${hasResponses
                            ? darkMode
                              ? 'bg-[#13983D]/20 text-[#13983D]'
                              : 'bg-[#13983D]/10 text-[#13983D]'
                            : darkMode
                              ? 'bg-[#F9B047]/20 text-[#F9B047]'
                              : 'bg-[#F9B047]/10 text-[#F9B047]'
                            }`}>
                            {hasResponses ? `${answeredCount}/${totalQuestions}` : 'Pendente'}
                          </span>
                        </td>
                        <td className={`px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          {hasResponses ? `${correctCount}/${answeredCount}` : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {hasResponses ? (
                            <div className="flex items-center gap-2">
                              <div className={`w-16 h-2 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                <div
                                  className={`h-full rounded-full ${percentage >= 70 ? 'bg-[#13983D]' :
                                    percentage >= 50 ? 'bg-[#F9B047]' : 'bg-[#E83A3C]'
                                    }`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {percentage}%
                              </span>
                            </div>
                          ) : (
                            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleOpenModal(student)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${hasResponses
                              ? darkMode
                                ? 'bg-[#0974BA]/20 text-[#0974BA] hover:bg-[#0974BA]/30'
                                : 'bg-[#0974BA]/10 text-[#0974BA] hover:bg-[#0974BA]/20'
                              : darkMode
                                ? 'bg-[#13983D]/20 text-[#13983D] hover:bg-[#13983D]/30'
                                : 'bg-[#13983D]/10 text-[#13983D] hover:bg-[#13983D]/20'
                              }`}
                          >
                            {hasResponses ? '✏️ Editar' : '➕ Cadastrar'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mensagem quando não há seleção */}
      {(!selectedExam || !selectedClass) && (
        <div className={`rounded-xl p-12 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-6xl mb-4">📝</div>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            Selecione uma prova e uma turma
          </h3>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Escolha uma prova com gabarito cadastrado e uma turma para ver os alunos e registrar suas respostas.
          </p>
        </div>
      )}

      {/* Modal de Registro de Respostas */}
      {showModal && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 overflow-y-auto">
          <div className={`w-full max-w-4xl my-8 rounded-2xl ${darkMode
            ? 'bg-slate-800 border border-[#0974BA]/30'
            : 'bg-white shadow-xl'
            }`}>
            {/* Header do Modal */}
            <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className={`text-xl font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    📝 Respostas do Aluno
                  </h2>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {selectedStudent.student_name} - Matrícula: {selectedStudent.student_serial}
                  </p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-[#0974BA]' : 'text-[#0974BA]'}`}>
                    Prova: {selectedExam.exam_code} - {selectedExam.exam_name}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className={`p-2 rounded-lg ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Resumo do resultado em tempo real */}
            <div className={`px-6 py-4 border-b ${darkMode ? 'border-slate-700 bg-slate-900/30' : 'border-slate-200 bg-slate-50'}`}>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>
                    {calculateResult.correct}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Corretas</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>
                    {calculateResult.wrong}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Erradas</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {calculateResult.blank}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Em branco</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${calculateResult.percentage >= 70
                    ? darkMode ? 'text-[#13983D]' : 'text-[#13983D]'
                    : calculateResult.percentage >= 50
                      ? darkMode ? 'text-[#F9B047]' : 'text-[#F9B047]'
                      : darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'
                    }`}>
                    {calculateResult.percentage}%
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Aproveitamento</div>
                </div>
              </div>
            </div>

            {/* Grid de Respostas */}
            <div className="p-6 max-h-[50vh] overflow-y-auto">
              {loadingAnswerKey ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-8 h-8 border-4 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-900/50' : 'bg-slate-50'}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {answerKey.map(question => {
                      const studentAnswer = studentAnswers[question.question_number];
                      const isCorrect = studentAnswer && studentAnswer === question.correct_answer;
                      const isWrong = studentAnswer && studentAnswer !== question.correct_answer;

                      return (
                        <div
                          key={question.question_number}
                          className={`rounded-lg p-3 ${darkMode
                            ? isCorrect ? 'bg-[#13983D]/10 border border-[#13983D]/30' :
                              isWrong ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/30' :
                                'bg-slate-800/80'
                            : isCorrect ? 'bg-[#13983D]/5 border border-[#13983D]/20' :
                              isWrong ? 'bg-[#E83A3C]/5 border border-[#E83A3C]/20' :
                                'bg-white border border-slate-200'
                            }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Q{question.question_number}
                            </span>
                            {studentAnswer && (
                              <span className={`text-xs ${isCorrect
                                ? darkMode ? 'text-[#13983D]' : 'text-[#13983D]'
                                : darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'
                                }`}>
                                {isCorrect ? '✓' : '✗'}
                              </span>
                            )}
                          </div>

                          {/* Botões de resposta */}
                          <div className="flex gap-1 justify-center">
                            {['A', 'B', 'C', 'D', 'E'].map(letter => {
                              const isSelected = studentAnswer === letter;
                              const isCorrectAnswer = letter === question.correct_answer;

                              return (
                                <button
                                  key={letter}
                                  onClick={() => handleSelectAnswer(question.question_number, letter)}
                                  className={`w-7 h-7 rounded text-xs font-bold transition-all ${isSelected
                                    ? isCorrect
                                      ? 'bg-[#13983D] text-white'
                                      : 'bg-[#E83A3C] text-white'
                                    : isCorrectAnswer && studentAnswer
                                      ? darkMode
                                        ? 'bg-[#13983D]/30 text-[#13983D] border border-[#13983D]'
                                        : 'bg-[#13983D]/10 text-[#13983D] border border-[#13983D]/30'
                                      : darkMode
                                        ? 'bg-slate-700/50 text-slate-400 hover:bg-slate-600 border border-slate-600'
                                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200 border border-slate-200'
                                    }`}
                                >
                                  {letter}
                                </button>
                              );
                            })}
                          </div>

                          {/* Indicador de gabarito */}
                          <div className={`mt-2 text-center text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Gabarito: <span className="font-bold">{question.correct_answer}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer do Modal */}
            <div className={`p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex justify-between items-center">
                <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {Object.keys(studentAnswers).length} de {answerKey.length} questões respondidas
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCloseModal}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                      ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                      }`}
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || Object.keys(studentAnswers).length === 0}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${saving || Object.keys(studentAnswers).length === 0
                      ? 'bg-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25'
                      }`}
                  >
                    {saving ? 'Salvando...' : '💾 Salvar Respostas'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RespostasAlunos;
